import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useContext } from 'react';
import type { TermProps } from '@fhnw/types/term';
import { VocabContext } from '@fhnw/components/VocabContext/VocabContext';

// Note it's essential to make this immutable, otherwise it will
// trigger a change all the time.
const emptyArray: TermProps[] = [];

type UseVocabOptions = {
  multilingual?: boolean; // Default: true
};

export const useVocab = (
  vocab_name: string,
  { multilingual = true }: UseVocabOptions = { multilingual: true },
) => {
  const dispatch = useDispatch();
  const { isVocabRequested, setVocabRequested } = useContext(VocabContext);

  const unilingualState = useSelector(
    (state: any) => state.vocabularies?.[vocab_name]?.items || emptyArray,
  );
  const multilingualState = useSelector(
    (state: any) =>
      state.vocabularies?.[vocab_name]?.subrequests?.[state.intl.locale]
        ?.items || emptyArray,
  );
  const items = multilingual ? multilingualState : unilingualState;

  const language = useSelector((state: any) => state.intl.locale);

  // Do not use this from useEffect, as it's bound to the
  // local variable, which is not updated. Use this only as
  // a dependency to re-trigger the dispath, in case the context
  // was resetted.
  const isCurrentVocabRequested = multilingual
    ? isVocabRequested(vocab_name, language)
    : isVocabRequested(vocab_name);

  useEffect(() => {
    if (
      items.length === 0 &&
      (multilingual
        ? !isVocabRequested(vocab_name, language)
        : !isVocabRequested(vocab_name))
    ) {
      if (multilingual) {
        setVocabRequested(vocab_name, language);
      } else {
        setVocabRequested(vocab_name);
      }
      dispatch(
        /* @ts-ignore */
        getVocabulary({
          vocabNameOrURL: vocab_name,
          size: -1,
          ...(multilingual ? { subrequest: language } : {}),
        }),
      );
    }
  }, [
    dispatch,
    items.length,
    isVocabRequested,
    setVocabRequested,
    vocab_name,
    isCurrentVocabRequested,
    language,
    multilingual,
  ]);

  return items;
};
